/**
 * Campaign Detail Page - View detailed campaign analytics with UTM breakdown
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Megaphone, Eye, Users, TrendingUp, Globe, Smartphone,
  Monitor, Tablet, Calendar, Target, Play, Pause, Edit2, Tag,
  BarChart3, Link2, MapPin, Clock, Nfc, QrCode, ExternalLink, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  active: { bg: 'bg-green-100', text: 'text-green-700' },
  paused: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-700' }
};

const StatCard = ({ icon: Icon, label, value, subvalue, color = 'primary' }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-card rounded-xl border border-border/50 p-5"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <div className="text-3xl font-bold">{value}</div>
    {subvalue && <div className="text-sm text-muted-foreground mt-1">{subvalue}</div>}
  </motion.div>
);

const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/campaigns/${campaignId}`);
      setCampaign(response.data);
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/api/admin/campaigns/${campaignId}`, { status: newStatus });
      toast.success(`Campaign ${newStatus}`);
      loadCampaign();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) return null;

  const stats = campaign.detailed_stats || {};
  const scanSources = stats.scan_sources || {};
  const devices = stats.devices || {};
  const locations = stats.top_locations || [];
  const utmData = stats.utm_breakdown || [];
  const dailyScans = stats.daily_scans || [];
  const statusStyle = STATUS_COLORS[campaign.status] || STATUS_COLORS.draft;

  const totalDevices = Object.values(devices).reduce((a, b) => a + b, 0) || 1;
  const totalSources = Object.values(scanSources).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: campaign.color + '20', color: campaign.color }}
                >
                  <Megaphone className="w-7 h-7" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{campaign.name}</h1>
                  {campaign.client_name && (
                    <p className="text-muted-foreground">Client: {campaign.client_name}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                  {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                </span>
                {campaign.status === 'draft' && (
                  <button
                    onClick={() => updateStatus('active')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
                  >
                    <Play className="w-4 h-4" /> Launch
                  </button>
                )}
                {campaign.status === 'active' && (
                  <button
                    onClick={() => updateStatus('paused')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-xl font-medium hover:bg-yellow-700"
                  >
                    <Pause className="w-4 h-4" /> Pause
                  </button>
                )}
                {campaign.status === 'paused' && (
                  <button
                    onClick={() => updateStatus('active')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
                  >
                    <Play className="w-4 h-4" /> Resume
                  </button>
                )}
              </div>
            </div>

            {campaign.description && (
              <p className="text-muted-foreground mt-4 max-w-2xl">{campaign.description}</p>
            )}

            {(campaign.start_date || campaign.end_date) && (
              <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {campaign.start_date && <span>From {campaign.start_date}</span>}
                {campaign.end_date && <span>to {campaign.end_date}</span>}
              </div>
            )}
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Eye}
              label="Total Scans"
              value={campaign.total_scans.toLocaleString()}
              color="blue"
            />
            <StatCard
              icon={Users}
              label="Unique Visitors"
              value={campaign.unique_visitors.toLocaleString()}
              color="purple"
            />
            <StatCard
              icon={TrendingUp}
              label="Engagement Rate"
              value={`${campaign.total_scans > 0 ? Math.round((campaign.unique_visitors / campaign.total_scans) * 100) : 0}%`}
              color="green"
            />
            <StatCard
              icon={Tag}
              label="Tags in Campaign"
              value={campaign.tag_count}
              color="orange"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Scan Sources */}
            <div className="bg-card rounded-2xl border border-border/50 p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary" />
                Scan Sources
              </h3>
              <div className="space-y-4">
                {[
                  { key: 'nfc', label: 'NFC Taps', icon: Nfc, color: 'blue' },
                  { key: 'qr', label: 'QR Scans', icon: QrCode, color: 'purple' },
                  { key: 'link', label: 'Direct Links', icon: ExternalLink, color: 'green' }
                ].map(({ key, label, icon: Icon, color }) => {
                  const count = scanSources[key] || 0;
                  const pct = Math.round((count / totalSources) * 100) || 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" /> {label}
                        </span>
                        <span className="font-medium">{count.toLocaleString()} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-${color}-500 rounded-full transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Device Breakdown */}
            <div className="bg-card rounded-2xl border border-border/50 p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-primary" />
                Device Types
              </h3>
              <div className="space-y-4">
                {[
                  { key: 'mobile', label: 'Mobile', icon: Smartphone, color: 'blue' },
                  { key: 'desktop', label: 'Desktop', icon: Monitor, color: 'purple' },
                  { key: 'tablet', label: 'Tablet', icon: Tablet, color: 'green' }
                ].map(({ key, label, icon: Icon, color }) => {
                  const count = devices[key] || 0;
                  const pct = Math.round((count / totalDevices) * 100) || 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" /> {label}
                        </span>
                        <span className="font-medium">{count.toLocaleString()} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-${color}-500 rounded-full transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* UTM Tracking */}
          {utmData.length > 0 && (
            <div className="bg-card rounded-2xl border border-border/50 p-6 mb-8">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                UTM Attribution
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Track which marketing channels are driving scans
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-medium">Source</th>
                      <th className="text-left py-3 px-2 font-medium">Medium</th>
                      <th className="text-left py-3 px-2 font-medium">Campaign</th>
                      <th className="text-right py-3 px-2 font-medium">Scans</th>
                      <th className="text-right py-3 px-2 font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {utmData.map((item, i) => {
                      const totalUtm = utmData.reduce((sum, u) => sum + u.count, 0);
                      const pct = Math.round((item.count / totalUtm) * 100);
                      return (
                        <tr key={i} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="py-3 px-2">{item.source || '-'}</td>
                          <td className="py-3 px-2">{item.medium || '-'}</td>
                          <td className="py-3 px-2">{item.campaign || '-'}</td>
                          <td className="py-3 px-2 text-right font-medium">{item.count}</td>
                          <td className="py-3 px-2 text-right">
                            <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                              {pct}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily Scans Chart */}
          {dailyScans.length > 0 && (
            <div className="bg-card rounded-2xl border border-border/50 p-6 mb-8">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Daily Performance
              </h3>
              <div className="h-48 flex items-end gap-1">
                {dailyScans.slice(-14).map((day, i) => {
                  const maxScans = Math.max(...dailyScans.map(d => d.scans));
                  const height = maxScans > 0 ? (day.scans / maxScans) * 100 : 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                        style={{ height: `${height}%`, minHeight: day.scans > 0 ? '4px' : '0' }}
                        title={`${day.date}: ${day.scans} scans`}
                      />
                      <span className="text-[10px] text-muted-foreground mt-1 rotate-45 origin-left">
                        {day.date.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top Locations */}
            {locations.length > 0 && (
              <div className="bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Top Locations
                </h3>
                <div className="space-y-3">
                  {locations.slice(0, 8).map((loc, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        {loc.city}, {loc.country}
                      </span>
                      <span className="font-medium">{loc.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags in Campaign */}
            {campaign.tag_details && campaign.tag_details.length > 0 && (
              <div className="bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-primary" />
                  Campaign Tags
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {campaign.tag_details.map((tag, i) => (
                    <div 
                      key={i} 
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-xl hover:bg-secondary cursor-pointer"
                      onClick={() => navigate(`/admin/analytics/${tag.nfc_code}`)}
                    >
                      <span className="font-medium">{tag.title || tag.nfc_code}</span>
                      <span className="text-xs text-muted-foreground">{tag.nfc_code}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* UTM Help */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <h3 className="font-semibold text-blue-900 mb-2">How to Use UTM Tracking</h3>
            <p className="text-sm text-blue-800 mb-3">
              Add UTM parameters to your QR codes or shared links to track which marketing channels drive the most engagement:
            </p>
            <code className="block bg-white p-3 rounded-lg text-xs text-blue-900 overflow-x-auto">
              {`${API.replace('/api', '')}/view/{tag_id}?utm_source=facebook&utm_medium=social&utm_campaign=${campaign.name.toLowerCase().replace(/\s+/g, '_')}`}
            </code>
            <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-blue-700">
              <div><strong>utm_source:</strong> Where traffic comes from (google, facebook, billboard)</div>
              <div><strong>utm_medium:</strong> Marketing medium (cpc, social, email, print)</div>
              <div><strong>utm_campaign:</strong> Campaign name (summer_sale, product_launch)</div>
              <div><strong>utm_content:</strong> Specific ad/content (banner_a, video_ad)</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CampaignDetailPage;
